import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type SortDir = "asc" | "desc";

export type ListState = {
  q: string;
  filters: Record<string, string>;
  sortKey: string;
  sortDir: SortDir;
  page: number;
  setQ: (q: string) => void;
  setFilter: (key: string, value: string) => void;
  setSort: (key: string, dir?: SortDir) => void;
  setPage: (page: number) => void;
  clear: () => void;
  activeCount: number;
};

/**
 * Search, filters, sort and page kept in the URL so a view can be shared or restored.
 * `prefix` keeps several lists on one page apart (e.g. "m" for modules). Other params (like ?tab=) are untouched.
 */
export function useListState(prefix: string, filterKeys: string[], defaultSort: { key: string; dir: SortDir }): ListState {
  const [params, setParams] = useSearchParams();
  const k = (name: string) => `${prefix}_${name}`;

  const q = params.get(k("q")) ?? "";
  const sortKey = params.get(k("sort")) ?? defaultSort.key;
  const sortDir = (params.get(k("dir")) as SortDir | null) ?? defaultSort.dir;
  const page = Math.max(1, Number(params.get(k("page")) ?? 1) || 1);
  const filterSig = filterKeys.map((f) => params.get(k(`f_${f}`)) ?? "").join("|");

  const filters = useMemo(() => {
    const out: Record<string, string> = {};
    filterKeys.forEach((f, i) => {
      out[f] = filterSig.split("|")[i] ?? "";
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSig]);

  const update = useCallback(
    (changes: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [name, value] of Object.entries(changes)) {
            if (value === null || value === "") next.delete(k(name));
            else next.set(k(name), value);
          }
          return next;
        },
        { replace: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setParams, prefix],
  );

  const setQ = useCallback((v: string) => update({ q: v, page: null }), [update]);
  const setFilter = useCallback((key: string, v: string) => update({ [`f_${key}`]: v, page: null }), [update]);
  const setSort = useCallback(
    (key: string, dir?: SortDir) => {
      const nextDir: SortDir = dir ?? (sortKey === key && sortDir === "asc" ? "desc" : "asc");
      update({ sort: key, dir: nextDir, page: null });
    },
    [update, sortKey, sortDir],
  );
  const setPage = useCallback((p: number) => update({ page: p > 1 ? String(p) : null }), [update]);
  const clear = useCallback(
    () => update({ q: null, page: null, ...Object.fromEntries(filterKeys.map((f) => [`f_${f}`, null])) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [update, filterSig],
  );

  const activeCount = (q ? 1 : 0) + Object.values(filters).filter(Boolean).length;
  return { q, filters, sortKey, sortDir, page, setQ, setFilter, setSort, setPage, clear, activeCount };
}
